import { memo, useState } from 'react';
import { Alert, Col, Dropdown, Popconfirm, Row, Space } from 'antd';
import { ExclamationCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { dateToString, formatPhoneNumber, getFullName } from '@/utils';
import {
  useChangeParentActiveMutation,
  useChangeParentPlacementMutation,
  useGetParentSummaryQuery,
} from '@/services/parent-api';
import { childCharacteristics } from '@/stores/data';
import { AddressCardSvg, EditSvg, EllipsisVSvg, FlagSvg, TrashSvg } from '@/assets/icons';
import Panel from '@/components/panel';
import CheckItem from '@/components/check-item';
import Button from '@/components/button';
import { ProfileCol, ProfileRow } from '@/components/profile-grid';
import InactiveModal from './inactive-modal';
import FlagModal from './flag-modal';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

const ParentInfo = ({ parentId, permissions }) => {
  const { data, isLoading, refetch } = useGetParentSummaryQuery({ parentId });
  const [changeActive, { isLoading: isChangingActive }] = useChangeParentActiveMutation();
  const [changePlacement, { isLoading: isChangingPlacement }] = useChangeParentPlacementMutation();

  const [isOpenInactiveModal, openInactiveModal] = useState(false);
  const [isOpenFlagModal, openFlagModal] = useState(false);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleChangeActive = async (reason) => {
    try {
      await changeActive({ parentId, body: { reason } }).unwrap();
      refetch();
    } catch (err) {}
  };

  const handleChangePlacement = async (reason) => {
    try {
      await changePlacement({ parentId, body: { reason } }).unwrap();
      refetch();
    } catch (err) {}
  };

  return (
    <Panel
      icon={<AddressCardSvg />}
      title="Parent Information"
      extra={
        permissions.writeParent &&
        data && (
          <Dropdown
            menu={{
              items: [
                (permissions.adminStaffAndSuper === false && permissions.writeParent === true) && {
                  key: 'edit',
                  icon: <EditSvg />,
                  label: <Link to="#" onClick={(e) => e.preventDefault()}
                  style={{
                    pointerEvents: 'none',
                    color: 'rgba(0, 0, 0, 0.25)',
                    cursor: 'not-allowed',
                  }}>Edit Parent</Link>,
                },
                permissions.adminStaffAndSuper && {
                  key: 'edit',
                  icon: <EditSvg />,
                  label: <Link to="../edit">Edit Parent</Link>,
                },
                !data.placementCapacityDate && (permissions.adminStaffAndSuper || permissions.writeParent) && {
                  key: 'flag',
                  icon: <FlagSvg />,
                  label: 'Set Flag',
                  onClick: () => openFlagModal(true),
                },
                !data.inactiveDate && permissions.adminStaffAndSuper && {
                  key: 'inactive',
                  danger: true,
                  icon: <TrashSvg />,
                  label: 'Make Inactive',
                  onClick: () => openInactiveModal(true),
                },
              ].filter((item) => item),
            }}
          >
            <Button type="link" icon={<EllipsisVSvg fontSize="16" />} />
          </Dropdown>
        )
      }
      loading={isLoading}
    >
      {data?.inactiveDate && (
        <Alert
          message="Inactive"
          type="error"
          showIcon
          icon={<ExclamationCircleFilled />}
          style={{ marginBottom: 8 }}
          action={
            permissions.adminStaffAndSuper && (
              <Popconfirm
                title="Are you sure you want to make this family active?"
                icon={<QuestionCircleOutlined />}
                okText="Active"
                onConfirm={() => handleChangeActive('')}
              >
                <Button size="small" style={{ width: 60 }} disabled={isChangingActive}>
                  Active
                </Button>
              </Popconfirm>
            )
          }
        />
      )}

      {data?.matchedDate && <Alert message="Matched" type="success" showIcon style={{ marginBottom: 8 }} />}
      {data?.adoptedWithinPast12Months === true && <Alert message="Adopted within past 12 months" type="info" showIcon  style={{
          marginBottom: 8,
          backgroundColor: '#e6f4ff',
          border: '1px solid #91caff',
          color: '#0958d9',
        }} />}

      {data?.placementCapacityDate && (
        <Alert
          message="Flagged"
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          action={
            (permissions.adminStaffAndSuper || permissions.writeParent) && (
              <Popconfirm
                title="Are you sure you want to remove flag?"
                icon={<QuestionCircleOutlined />}
                okText="Remove"
                onConfirm={() => handleChangePlacement('')}
              >
                <Button size="small" style={{ width: 60 }} disabled={isChangingPlacement}>
                  Unflag
                </Button>
              </Popconfirm>
            )
          }
        />
      )}

      {data && (
        <Row gutter={[24, 32]}>
          <Col md={24} lg={12}>
            <ProfileRow title={getFullName(data.primary.name)} style={{ rowGap: '0px' }}>
              <ProfileCol label="Birthday">
                {data.primary.birthInfo.date && (
                  <>
                    {dateToString(data.primary.birthInfo.date)} <i>({data.primary.birthInfo.age} years)</i>
                  </>
                )}
              </ProfileCol>
              <ProfileCol label="Race" value={data.primary.race.name || 'None'} />
            </ProfileRow>
            {data.significantOther && (
              <ProfileRow title={getFullName(data.significantOther.name)} style={{ marginTop: 15, rowGap: '0px' }}>
                <ProfileCol label="Birthday">
                  {data.significantOther.birthInfo.date && (
                    <>
                      {dateToString(data.significantOther.birthInfo.date)}{' '}
                      <i>({data.significantOther.birthInfo.age} years)</i>
                    </>
                  )}
                </ProfileCol>
                <ProfileCol label="Race" value={data.significantOther.race.name || 'None'} />
              </ProfileRow>
            )}
            <ProfileRow style={{ marginTop: 15 }}>
              <ProfileCol>
                <Space direction="vertical" size={6}>
                  <div>Characteristics</div>
                  {childCharacteristics.map((item) => (
                    <CheckItem key={item.key} value={data.characteristics[item.key]} label={item.label} />
                  ))}
                </Space>
              </ProfileCol>
            </ProfileRow>
          </Col>

          <Col md={24} lg={12}>
            <ProfileRow>
              <ProfileCol
                label="Address"
                value={[
                  data.address.line1,
                  data.address.line2,
                  `${data.address.city}, ${data.address.state} ${data.address.zip}`,
                  data.county.name,
                ]
                  .filter((s) => s)
                  .join('\n')}
              />

              {data.contact.email.primary && <ProfileCol label="Primary Email" value={data.contact.email.primary} />}
              {data.contact.email.secondary && (
                <ProfileCol label="Secondary Email" value={data.contact.email.secondary} />
              )}
              {data.contact.email.magikEmail && (
                <ProfileCol label="Magik Email" value={data.contact.email.magikEmail} />
              )}

              {data.contact.phones.map((item) => (
                <ProfileCol
                  key={item.phoneType.id}
                  label={`${item.phoneType.name} ${item.phoneType.code === 'fax' ? '' : 'Phone'}`}
                  value={`${formatPhoneNumber(item.number)}${item.ext ? ` x ${item.ext}` : ''}`}
                />
              ))}

              <ProfileCol
                label="Adoption Liaison"
                value={`${getFullName(data.adoptionLiaison.name)} (${data.adoptionLiaison.emailAddress})`}
              />
            </ProfileRow>
            <ProfileRow style={{ marginTop: 15 }}>
              <ProfileCol>
                <div>Other: {data.characteristics.otherCharacteristic || 'None'}</div>
              </ProfileCol>
            </ProfileRow>
          </Col>
        </Row>
      )}

      <InactiveModal
        open={isOpenInactiveModal}
        loading={isChangingActive}
        onSave={handleChangeActive}
        onClose={() => openInactiveModal(false)}
      />

      <FlagModal
        open={isOpenFlagModal}
        loading={isChangingPlacement}
        onSave={handleChangePlacement}
        onClose={() => openFlagModal(false)}
      />
    </Panel>
  );
};

export default memo(ParentInfo);
